'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import dynamic from 'next/dynamic';
import Icon from '@/components/ui/AppIcon';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';
import { useChat } from '@/lib/hooks/useChat';
import { getChatCompletion } from '@/lib/ai/chatCompletion';
import { fetchAiScoresBatch, fetchCityBrief, fetchMarketFundamentals, fetchStormIntelligence } from '@/lib/ai/geminiCache';
import type { CityPin, CityStatus, RadiusRing, OverlayLayers, HailSpot, CensusDataPoint, CompetitorLocation, CityBusinessData, CrewAssignment, StormPin } from './ExpansionMap';
import MarketSearchPanel, { type SearchedMarket } from './MarketSearchPanel';
import ExpansionPriorityList, { type ExpansionPriorityItem } from './ExpansionPriorityList';
import ClusterAnalysisPanel from './ClusterAnalysisPanel';
import CompetitorLocationsManager from './CompetitorLocationsManager';
import MarketIntelligencePanel from './MarketIntelligencePanel';
import CityCompetitorSearch from './CityCompetitorSearch';
import type { ManualCompetitor } from './CompetitorLocationsManager';
import CompetitorVisualizationCharts from './CompetitorVisualizationCharts';
import ThreatTierSystem from './ThreatTierSystem';
import MarketValidationPanel from './MarketValidationPanel';

// ─── Dynamic imports (SSR disabled for Leaflet) ────────────────────────────────
const ExpansionMap = dynamic(() => import('./ExpansionMap'), {
  ssr: false,
  loading: () => (
    <div className="flex items-center justify-center h-full rounded-xl" style={{ background: '#f1f5f9' }}>
      <div className="flex flex-col items-center gap-3">
        <div className="w-10 h-10 rounded-full border-[3px] border-[#E89A3C] border-t-transparent animate-spin" />
        <span className="text-sm text-gray-500 font-medium">Loading Expansion Map...</span>
      </div>
    </div>
  ),
});

const SupplierMap = dynamic(() => import('./SupplierMap'), {
  ssr: false,
  loading: () => (
    <div className="flex items-center justify-center h-full rounded-lg" style={{ background: '#f1f5f9' }}>
      <div className="w-6 h-6 rounded-full border-2 border-[#E89A3C] border-t-transparent animate-spin" />
    </div>
  ),
});

// ─── Types ────────────────────────────────────────────────────────────────────
interface LaborPool {
  estimated_crews: number;
  avg_labor_cost_per_square: number;
  crew_availability: string;
}

interface Compliance {
  gc_license_required: boolean;
  state_insurance_minimum: string;
  ice_water_shield_mandatory: boolean;
  drip_edge_required: boolean;
  max_shingle_layers: number;
  avg_permit_days: number;
}

interface SupplierLocation {
  name: string;
  lat: number;
  lng: number;
  miles: number;
}

interface ExpansionMarket {
  id: string;
  city: string;
  state: string;
  viability_score: number;
  population_growth: number;
  median_home_age: number;
  demographic_match: number;
  hail_events_5yr: number;
  annual_replacements_needed: number;
  labor_pool: LaborPool;
  compliance: Compliance;
  supplier_locations: SupplierLocation[];
  is_active_market: boolean;
  is_recommended: boolean;
}

// ─── Status config ────────────────────────────────────────────────────────────
const STATUS_CONFIG: Record<CityStatus, { color: string; label: string; emoji: string; bg: string }> = {
  'active-market':   { color: '#10B981', label: 'Active Market',      emoji: '✅', bg: '#ECFDF5' },
  'launching':       { color: '#8B5CF6', label: 'Launching',          emoji: '🚀', bg: '#F5F3FF' },
  'target-market':   { color: '#E89A3C', label: 'Target Market',      emoji: '🎯', bg: '#EFF6FF' },
  'prospecting':     { color: '#F59E0B', label: 'Prospecting',        emoji: '🔍', bg: '#FEF3C7' },
  'paused':          { color: '#22C55E', label: 'Favorable Market',   emoji: '📈', bg: '#F0FDF4' },
  'competitor-held': { color: '#EF4444', label: 'Competitor-Held',    emoji: '🚫', bg: '#FEE2E2' },
};

const RING_CONFIG: Record<RadiusRing, { color: string; label: string }> = {
  10: { color: '#E89A3C', label: '10 mi' },
  25: { color: '#F59E0B', label: '25 mi' },
  50: { color: '#EF4444', label: '50 mi' },
};

const ALL_RADII: RadiusRing[] = [10, 25, 50];

// ─── State coordinates for map ────────────────────────────────────────────────
const STATE_COORDS: Record<string, { lat: number; lng: number }> = {
  Columbus:  { lat: 39.9612, lng: -82.9988 },
  Dallas:    { lat: 32.7767, lng: -96.7970 },
  Denver:    { lat: 39.7392, lng: -104.9903 },
  Atlanta:   { lat: 33.7490, lng: -84.3880 },
  Charlotte: { lat: 35.2271, lng: -80.8431 },
  Nashville: { lat: 36.1627, lng: -86.7816 },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const getScoreColor = (score: number) => {
  if (score >= 85) return '#10B981';
  if (score >= 70) return '#F59E0B';
  return '#EF4444';
};

const getScoreLabel = (score: number) => {
  if (score >= 85) return 'High Viability';
  if (score >= 70) return 'Moderate';
  return 'Saturated';
};

const formatNumber = (n: number) => n.toLocaleString();

// ─── AI Market Insight Card ───────────────────────────────────────────────────
interface AIMarketInsightProps {
  market: ExpansionMarket;
}

function AIMarketInsight({ market }: AIMarketInsightProps) {
  const [insight, setInsight] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const { response, isLoading, sendMessage } = useChat('GEMINI', 'gemini/gemini-2.5-flash', false);
  const fetched = useState(false);
  const hasFetched = fetched[0];
  const setHasFetched = fetched[1];

  const cacheKey = `market_insight_${market.id}`;

  const generateInsight = useCallback(() => {
    if (hasFetched) return;
    setHasFetched(true);

    try {
      const cached = sessionStorage.getItem(cacheKey);
      if (cached) {
        const { text, ts } = JSON.parse(cached);
        if (Date.now() - ts < 60 * 60 * 1000) {
          setInsight(text);
          return;
        }
      }
    } catch { /* ignore */ }

    setLoading(true);
    sendMessage([
      {
        role: 'system',
        content: 'You are a roofing market expansion analyst for NextRoof 360. Generate a concise 2-3 sentence AI market insight for the given city. Focus on the most compelling opportunity signals and any key risks. Be specific with the data provided. Write in a professional, direct tone.',
      },
      {
        role: 'user',
        content: `Analyze this expansion market for NextRoof 360:
City: ${market.city}, ${market.state}
Viability Score: ${market.viability_score}/100
Population Growth: +${market.population_growth}%
Median Home Age: ${market.median_home_age} years
Hail Events (5yr): ${market.hail_events_5yr}
Annual Replacements Needed: ${formatNumber(market.annual_replacements_needed)}
Labor Pool: ${market.labor_pool.estimated_crews} crews, ${market.labor_pool.crew_availability} availability, $${market.labor_pool.avg_labor_cost_per_square}/sq
GC License Required: ${market.compliance.gc_license_required ? 'Yes' : 'No'}
Avg Permit Days: ${market.compliance.avg_permit_days}`,
      },
    ], { max_completion_tokens: 150 });
  }, [market, hasFetched, setHasFetched, sendMessage, cacheKey]);

  useEffect(() => {
    generateInsight();
  }, [generateInsight]);

  useEffect(() => {
    if (response && !isLoading) {
      setInsight(response);
      setLoading(false);
      try {
        sessionStorage.setItem(cacheKey, JSON.stringify({ text: response, ts: Date.now() }));
      } catch { /* ignore */ }
    }
  }, [response, isLoading, cacheKey]);

  const isGenerating = loading || isLoading;

  return (
    <div
      className="mx-5 mb-4 rounded-xl p-4"
      style={{ background: 'linear-gradient(135deg, #EFF6FF 0%, #F0FDF4 100%)', border: '1px solid #DBEAFE' }}
    >
      <div className="flex items-center gap-2 mb-2">
        <div className="w-6 h-6 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #E89A3C 0%, #1C2E5E 100%)' }}>
          <Icon name="SparklesIcon" size={12} className="text-white" />
        </div>
        <span className="text-xs font-bold uppercase tracking-wide" style={{ color: '#1C2E5E' }}>
          SmartPick™ AI Insight
        </span>
        {isGenerating && (
          <div className="w-3 h-3 rounded-full border border-[#E89A3C] border-t-transparent animate-spin ml-auto" />
        )}
      </div>
      {isGenerating ? (
        <div className="space-y-2">
          <div className="h-3 rounded-full animate-pulse" style={{ background: 'rgba(3,52,148,0.1)', width: '95%' }} />
          <div className="h-3 rounded-full animate-pulse" style={{ background: 'rgba(3,52,148,0.07)', width: '80%' }} />
          <div className="h-3 rounded-full animate-pulse" style={{ background: 'rgba(3,52,148,0.05)', width: '88%' }} />
        </div>
      ) : insight ? (
        <p className="text-xs leading-relaxed" style={{ color: '#1E3A5F' }}>{insight}</p>
      ) : null}
    </div>
  );
}

// ─── AI City Brief Panel ──────────────────────────────────────────────────────
interface AICityBriefProps {
  pin: CityPin;
  onClose?: () => void;
}

function AICityBrief({ pin, onClose }: AICityBriefProps) {
  const [brief, setBrief] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const hasFetched = useRef(false);

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;

    setLoading(true);
    fetchCityBrief(pin.id, pin.city_name, pin.state, pin.city_status, pin.radius_rings)
      .then((text) => {
        setBrief(text);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  }, [pin.id, pin.city_name, pin.state, pin.city_status, pin.radius_rings]);

  return (
    <div
      className="rounded-xl p-3 mt-3"
      style={{ background: 'linear-gradient(135deg, #EFF6FF 0%, #F0FDF4 100%)', border: '1px solid #DBEAFE' }}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <div className="w-5 h-5 rounded-md flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #E89A3C 0%, #1C2E5E 100%)' }}>
            <Icon name="SparklesIcon" size={10} className="text-white" />
          </div>
          <span className="text-[10px] font-bold uppercase tracking-wide" style={{ color: '#1C2E5E' }}>
            Gemini Market Brief
          </span>
          {loading && (
            <div className="w-3 h-3 rounded-full border border-[#E89A3C] border-t-transparent animate-spin" />
          )}
        </div>
      </div>
      {loading ? (
        <div className="space-y-1.5">
          <div className="h-2.5 rounded-full animate-pulse" style={{ background: 'rgba(3,52,148,0.1)', width: '95%' }} />
          <div className="h-2.5 rounded-full animate-pulse" style={{ background: 'rgba(3,52,148,0.07)', width: '80%' }} />
          <div className="h-2.5 rounded-full animate-pulse" style={{ background: 'rgba(3,52,148,0.05)', width: '88%' }} />
        </div>
      ) : brief ? (
        <p className="text-[11px] leading-relaxed" style={{ color: '#1E3A5F' }}>{brief}</p>
      ) : null}
    </div>
  );
}

// ─── Market Fundamentals Panel ────────────────────────────────────────────────
interface MarketFundamentalsPanelProps {
  pin: CityPin;
}

function MarketFundamentalsPanel({ pin }: MarketFundamentalsPanelProps) {
  const [data, setData] = useState<{
    totalAddressableMarket: string;
    medianHomeAge: string;
    homeownershipRate: string;
    avgHomeValueRange: string;
    populationGrowthTrend: string;
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const hasFetched = useRef(false);

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    setLoading(true);
    fetchMarketFundamentals(pin.id, pin.city_name, pin.state)
      .then((d) => { setData(d); setLoading(false); })
      .catch(() => { setLoading(false); });
  }, [pin.id, pin.city_name, pin.state]);

  const rows = data ? [
    { icon: 'BuildingOffice2Icon', label: 'Total Addressable Market', value: data.totalAddressableMarket },
    { icon: 'HomeIcon', label: 'Median Home Age', value: data.medianHomeAge },
    { icon: 'UserGroupIcon', label: 'Homeownership Rate', value: data.homeownershipRate },
    { icon: 'CurrencyDollarIcon', label: 'Avg Home Value Range', value: data.avgHomeValueRange },
    { icon: 'ArrowTrendingUpIcon', label: 'Population Growth Trend', value: data.populationGrowthTrend },
  ] : [];

  return (
    <div className="rounded-xl overflow-hidden" style={{ border: '1px solid #E0E7FF' }}>
      <div className="flex items-center gap-2 px-3 py-2.5" style={{ background: 'linear-gradient(135deg, #EEF2FF 0%, #E0E7FF 100%)' }}>
        <div className="w-5 h-5 rounded-md flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #6366F1 0%, #4338CA 100%)' }}>
          <Icon name="ChartBarIcon" size={10} className="text-white" />
        </div>
        <span className="text-[10px] font-bold uppercase tracking-wide" style={{ color: '#3730A3' }}>Market Fundamentals</span>
        {loading && <div className="w-3 h-3 rounded-full border border-[#6366F1] border-t-transparent animate-spin ml-auto" />}
      </div>
      <div className="divide-y" style={{ divideColor: '#F3F4F6' }}>
        {loading ? (
          <div className="p-3 space-y-2">
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} className="flex justify-between items-center">
                <div className="h-2.5 rounded-full animate-pulse" style={{ background: '#E0E7FF', width: '45%' }} />
                <div className="h-2.5 rounded-full animate-pulse" style={{ background: '#E0E7FF', width: '30%' }} />
              </div>
            ))}
          </div>
        ) : rows.map((row, i) => (
          <div key={i} className="flex items-center justify-between px-3 py-2" style={{ background: i % 2 === 0 ? '#FAFAFA' : '#fff' }}>
            <div className="flex items-center gap-1.5">
              <Icon name={row.icon as any} size={11} style={{ color: '#6366F1' }} />
              <span className="text-[10px] font-medium" style={{ color: '#6B7280' }}>{row.label}</span>
            </div>
            <span className="text-[10px] font-bold text-right ml-2" style={{ color: '#1E1B4B', maxWidth: '55%' }}>{row.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Storm Intelligence Panel ─────────────────────────────────────────────────
interface StormIntelligencePanelProps {
  pin: CityPin;
}

function StormIntelligencePanel({ pin }: StormIntelligencePanelProps) {
  const [data, setData] = useState<{
    hailEventFrequency: string;
    lastMajorStormEvent: string;
    stormSeasonPeakMonths: string;
    insuranceClaimDensity: string;
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const hasFetched = useRef(false);

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    setLoading(true);
    fetchStormIntelligence(pin.id, pin.city_name, pin.state)
      .then((d) => { setData(d); setLoading(false); })
      .catch(() => { setLoading(false); });
  }, [pin.id, pin.city_name, pin.state]);

  const rows = data ? [
    { icon: 'CloudIcon', label: 'Hail Event Frequency', value: data.hailEventFrequency, accent: '#0EA5E9' },
    { icon: 'BoltIcon', label: 'Last Major Storm Event', value: data.lastMajorStormEvent, accent: '#F59E0B' },
    { icon: 'CalendarDaysIcon', label: 'Storm Season Peak Months', value: data.stormSeasonPeakMonths, accent: '#8B5CF6' },
    { icon: 'DocumentTextIcon', label: 'Insurance Claim Density', value: data.insuranceClaimDensity, accent: '#EF4444' },
  ] : [];

  return (
    <div className="rounded-xl overflow-hidden" style={{ border: '1px solid #BAE6FD' }}>
      <div className="flex items-center gap-2 px-3 py-2.5" style={{ background: 'linear-gradient(135deg, #E0F2FE 0%, #BAE6FD 100%)' }}>
        <div className="w-5 h-5 rounded-md flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #0EA5E9 0%, #0369A1 100%)' }}>
          <Icon name="CloudIcon" size={10} className="text-white" />
        </div>
        <span className="text-[10px] font-bold uppercase tracking-wide" style={{ color: '#0C4A6E' }}>Storm Intelligence</span>
        {loading && <div className="w-3 h-3 rounded-full border border-[#0EA5E9] border-t-transparent animate-spin ml-auto" />}
      </div>
      <div className="divide-y" style={{ divideColor: '#F3F4F6' }}>
        {loading ? (
          <div className="p-3 space-y-2">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="flex justify-between items-center">
                <div className="h-2.5 rounded-full animate-pulse" style={{ background: '#BAE6FD', width: '45%' }} />
                <div className="h-2.5 rounded-full animate-pulse" style={{ background: '#BAE6FD', width: '30%' }} />
              </div>
            ))}
          </div>
        ) : rows.map((row, i) => (
          <div key={i} className="flex items-center justify-between px-3 py-2" style={{ background: i % 2 === 0 ? '#F0F9FF' : '#fff' }}>
            <div className="flex items-center gap-1.5">
              <Icon name={row.icon as any} size={11} style={{ color: row.accent }} />
              <span className="text-[10px] font-medium" style={{ color: '#6B7280' }}>{row.label}</span>
            </div>
            <span className="text-[10px] font-bold text-right ml-2" style={{ color: '#0C4A6E', maxWidth: '55%' }}>{row.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Add City Modal ───────────────────────────────────────────────────────────
interface AddCityModalProps {
  lat: number;
  lng: number;
  onSave: (pin: CityPin) => void;
  onCancel: () => void;
  saving: boolean;
}

function AddCityModal({ lat, lng, onSave, onCancel, saving }: AddCityModalProps) {
  const [cityName, setCityName] = useState('');
  const [state, setState] = useState('');
  const [status, setStatus] = useState<CityStatus>('target-market');
  const [rings, setRings] = useState<RadiusRing[]>([]);

  const toggleRing = (r: RadiusRing) => {
    setRings((prev) => prev.includes(r) ? prev.filter((x) => x !== r) : [...prev, r]);
  };

  const handleSave = () => {
    if (!cityName.trim() || !state.trim()) return;
    onSave({
      id: '',
      city_name: cityName.trim(),
      state: state.trim().toUpperCase().slice(0, 2),
      latitude: lat,
      longitude: lng,
      city_status: status,
      radius_rings: rings,
    });
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.5)' }}>
      <div className="rounded-2xl w-full max-w-md mx-4 overflow-hidden" style={{ background: '#fff', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
        {/* Header */}
        <div className="px-6 py-4 flex items-center justify-between" style={{ background: 'linear-gradient(135deg, #1C2E5E 0%, #1C2E5E 100%)' }}>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'rgba(232,154,60,0.2)' }}>
              <Icon name="MapPinIcon" size={16} className="text-white" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-white">Add Target Market</h3>
              <p className="text-xs" style={{ color: 'rgba(255,255,255,0.6)' }}>
                {lat.toFixed(4)}, {lng.toFixed(4)}
              </p>
            </div>
          </div>
          <button onClick={onCancel} className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-white/10" style={{ color: 'rgba(255,255,255,0.7)' }}>
            <Icon name="XMarkIcon" size={16} />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {/* City + State */}
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <label className="block text-xs font-semibold mb-1.5" style={{ color: '#374151' }}>City Name *</label>
              <input
                type="text"
                value={cityName}
                onChange={(e) => setCityName(e.target.value)}
                placeholder="e.g. Cincinnati"
                className="w-full px-3 py-2 rounded-lg text-sm border outline-none focus:ring-2"
                style={{ borderColor: '#E5E7EB', color: '#1C2E5E', fontFamily: 'Inter, sans-serif' }}
                autoFocus
              />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1.5" style={{ color: '#374151' }}>State *</label>
              <input
                type="text"
                value={state}
                onChange={(e) => setState(e.target.value)}
                placeholder="OH"
                maxLength={2}
                className="w-full px-3 py-2 rounded-lg text-sm border outline-none focus:ring-2 uppercase"
                style={{ borderColor: '#E5E7EB', color: '#1C2E5E', fontFamily: 'Inter, sans-serif' }}
              />
            </div>
          </div>

          {/* Status Tier */}
          <div>
            <label className="block text-xs font-semibold mb-2" style={{ color: '#374151' }}>City Status Tier</label>
            <div className="grid grid-cols-1 gap-2">
              {(Object.entries(STATUS_CONFIG) as [CityStatus, typeof STATUS_CONFIG[CityStatus]][]).map(([key, cfg]) => (
                <button
                  key={key}
                  onClick={() => setStatus(key)}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-all"
                  style={{
                    background: status === key ? cfg.bg : '#F9FAFB',
                    border: `2px solid ${status === key ? cfg.color : '#E5E7EB'}`,
                  }}
                >
                  <span className="text-base">{cfg.emoji}</span>
                  <span className="text-xs font-semibold" style={{ color: status === key ? cfg.color : '#374151' }}>{cfg.label}</span>
                  {status === key && (
                    <div className="ml-auto w-4 h-4 rounded-full flex items-center justify-center" style={{ background: cfg.color }}>
                      <Icon name="CheckIcon" size={10} className="text-white" />
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Radius Rings */}
          <div>
            <label className="block text-xs font-semibold mb-2" style={{ color: '#374151' }}>Territory Radius Rings</label>
            <div className="flex gap-2">
              {ALL_RADII.map((r) => {
                const rc = RING_CONFIG[r];
                const active = rings.includes(r);
                return (
                  <button
                    key={r}
                    onClick={() => toggleRing(r)}
                    className="flex-1 py-2 rounded-xl text-xs font-bold transition-all"
                    style={{
                      background: active ? rc.color : '#F3F4F6',
                      color: active ? '#fff' : '#6B7280',
                      border: `2px solid ${active ? rc.color : '#E5E7EB'}`,
                    }}
                  >
                    {rc.label}
                  </button>
                );
              })}
            </div>
            <p className="text-xs mt-1.5" style={{ color: '#9CA3AF' }}>Select rings to draw service zone circles around this city</p>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-1">
            <button
              onClick={onCancel}
              className="flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all hover:bg-gray-100"
              style={{ color: '#6B7280', border: '1px solid #E5E7EB' }}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!cityName.trim() || !state.trim() || saving}
              className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2"
              style={{ background: '#1C2E5E' }}
            >
              {saving ? (
                <div className="w-4 h-4 rounded-full border-2 border-white border-t-transparent animate-spin" />
              ) : (
                <Icon name="MapPinIcon" size={14} />
              )}
              {saving ? 'Saving...' : 'Drop Pin'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Pin Detail Panel ─────────────────────────────────────────────────────────
interface CityPipelineStats {
  jobCount: number;
  revenue: number;
  crewCount: number;
  loading: boolean;
}

interface PinDetailPanelProps {
  pin: CityPin;
  onClose: () => void;
  onStatusChange: (id: string, status: CityStatus) => void;
  onRingToggle: (id: string, ring: RadiusRing) => void;
  onDelete: (id: string) => void;
  saving: boolean;
  pipelineStats: CityPipelineStats;
  showAiIntelligence: boolean;
  aiScore?: number;
}

function PinDetailPanel({ pin, onClose, onStatusChange, onRingToggle, onDelete, saving, pipelineStats, showAiIntelligence, aiScore }: PinDetailPanelProps) {
  const cfg = STATUS_CONFIG[pin.city_status] ?? STATUS_CONFIG['target-market'];

  const formatCurrency = (n: number) =>
    n >= 1000000
      ? `$${(n / 1000000).toFixed(1)}M`
      : n >= 1000
      ? `$${(n / 1000).toFixed(0)}K`
      : `$${n.toLocaleString()}`;

  const getAiScoreColor = (score: number) => {
    if (score >= 75) return '#10B981';
    if (score >= 50) return '#F59E0B';
    return '#EF4444';
  };

  return (
    <div
      className="rounded-2xl overflow-hidden"
      style={{ background: '#fff', border: '1px solid #E5E7EB', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3" style={{ background: 'linear-gradient(135deg, #1C2E5E 0%, #1C2E5E 100%)' }}>
        <div className="flex items-center gap-2">
          <span className="text-lg">{cfg.emoji}</span>
          <div>
            <div className="text-sm font-bold text-white">{pin.city_name}, {pin.state}</div>
            <div className="text-xs" style={{ color: cfg.color }}>{cfg.label}</div>
          </div>
        </div>
        <button onClick={onClose} className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-white/10" style={{ color: 'rgba(255,255,255,0.7)' }}>
          <Icon name="XMarkIcon" size={16} />
        </button>
      </div>

      {/* ── AI Score Badge (when AI Intelligence is on) ── */}
      {showAiIntelligence && aiScore != null && (
        <div className="px-4 pt-3">
          <div
            className="flex items-center justify-between rounded-xl px-3 py-2"
            style={{ background: 'linear-gradient(135deg, #EFF6FF 0%, #F0FDF4 100%)', border: '1px solid #DBEAFE' }}
          >
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 rounded-md flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #E89A3C 0%, #1C2E5E 100%)' }}>
                <Icon name="SparklesIcon" size={10} className="text-white" />
              </div>
              <span className="text-[10px] font-bold uppercase tracking-wide" style={{ color: '#1C2E5E' }}>AI Market Score</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-lg font-black" style={{ color: getAiScoreColor(aiScore) }}>{aiScore}</span>
              <span className="text-[10px] font-semibold" style={{ color: '#9CA3AF' }}>/100</span>
            </div>
          </div>
        </div>
      )}

      {/* ── Pipeline Stats ── */}
      <div className="px-4 pt-3 pb-3 border-b" style={{ borderColor: '#F3F4F6' }}>
        <div className="text-xs font-bold uppercase tracking-wide mb-2.5" style={{ color: '#6B7280' }}>Pipeline Intelligence</div>
        {pipelineStats.loading ? (
          <div className="grid grid-cols-3 gap-2">
            {[0, 1, 2].map((i) => (
              <div key={i} className="rounded-xl p-2.5 animate-pulse" style={{ background: '#F3F4F6', height: 60 }} />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-2">
            <div className="rounded-xl p-2.5 text-center" style={{ background: '#EFF6FF', border: '1px solid #DBEAFE' }}>
              <div className="text-lg font-black leading-none" style={{ color: '#1C2E5E' }}>{pipelineStats.jobCount}</div>
              <div className="text-[10px] font-semibold mt-1 leading-tight" style={{ color: '#6B7280' }}>Jobs</div>
            </div>
            <div className="rounded-xl p-2.5 text-center" style={{ background: '#ECFDF5', border: '1px solid #A7F3D0' }}>
              <div className="text-base font-black leading-none" style={{ color: '#10B981' }}>{formatCurrency(pipelineStats.revenue)}</div>
              <div className="text-[10px] font-semibold mt-1 leading-tight" style={{ color: '#6B7280' }}>Revenue</div>
            </div>
            <div className="rounded-xl p-2.5 text-center" style={{ background: '#FEF3C7', border: '1px solid #FDE68A' }}>
              <div className="text-lg font-black leading-none" style={{ color: '#D97706' }}>{pipelineStats.crewCount}</div>
              <div className="text-[10px] font-semibold mt-1 leading-tight" style={{ color: '#6B7280' }}>Crews</div>
            </div>
          </div>
        )}
      </div>

      <div className="p-4 space-y-4">
        {/* AI City Brief (when AI Intelligence is on) */}
        {showAiIntelligence && (
          <AICityBrief pin={pin} />
        )}

        {/* Market Fundamentals (when AI Intelligence is on) */}
        {showAiIntelligence && (
          <MarketFundamentalsPanel pin={pin} />
        )}

        {/* Storm Intelligence (when AI Intelligence is on) */}
        {showAiIntelligence && (
          <StormIntelligencePanel pin={pin} />
        )}

        {/* Status Tier */}
        <div>
          <div className="text-xs font-bold uppercase tracking-wide mb-2" style={{ color: '#6B7280' }}>Change Status</div>
          <div className="grid grid-cols-1 gap-1.5">
            {(Object.entries(STATUS_CONFIG) as [CityStatus, typeof STATUS_CONFIG[CityStatus]][]).map(([key, c]) => (
              <button
                key={key}
                onClick={() => onStatusChange(pin.id, key)}
                disabled={saving}
                className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-left transition-all"
                style={{
                  background: pin.city_status === key ? c.bg : 'transparent',
                  border: `1.5px solid ${pin.city_status === key ? c.color : '#E5E7EB'}`,
                }}
              >
                <span className="text-sm">{c.emoji}</span>
                <span className="text-xs font-semibold" style={{ color: pin.city_status === key ? c.color : '#374151' }}>{c.label}</span>
                {pin.city_status === key && <div className="ml-auto w-3 h-3 rounded-full" style={{ background: c.color }} />}
              </button>
            ))}
          </div>
        </div>

        {/* Radius Rings */}
        <div>
          <div className="text-xs font-bold uppercase tracking-wide mb-2" style={{ color: '#6B7280' }}>Territory Rings</div>
          <div className="flex gap-2">
            {ALL_RADII.map((r) => {
              const rc = RING_CONFIG[r];
              const active = (pin.radius_rings ?? []).includes(r);
              return (
                <button
                  key={r}
                  onClick={() => onRingToggle(pin.id, r)}
                  disabled={saving}
                  className="flex-1 py-2 rounded-xl text-xs font-bold transition-all"
                  style={{
                    background: active ? rc.color : '#F3F4F6',
                    color: active ? '#fff' : '#6B7280',
                    border: `2px solid ${active ? rc.color : '#E5E7EB'}`,
                  }}
                >
                  {rc.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Delete */}
        <button
          onClick={() => onDelete(pin.id)}
          disabled={saving}
          className="w-full py-2 rounded-xl text-xs font-semibold transition-all hover:opacity-90 flex items-center justify-center gap-1.5"
          style={{ background: '#FEE2E2', color: '#EF4444', border: '1px solid #FECACA' }}
        >
          <Icon name="TrashIcon" size={12} />
          Remove Pin
        </button>
      </div>
    </div>
  );
}

// ─── AI Recommendation Banner ─────────────────────────────────────────────────
interface AIRecommendationBannerProps {
  cityPins: CityPin[];
}

function AIRecommendationBanner({ cityPins }: AIRecommendationBannerProps) {
  const [recommendation, setRecommendation] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const hasFetched = useRef(false);

  useEffect(() => {
    if (hasFetched.current || cityPins.length === 0) return;
    hasFetched.current = true;

    const cacheKey = `ai_expansion_recommendation_${cityPins.map(p => p.id).sort().join('_')}`;
    try {
      const cached = sessionStorage.getItem(cacheKey);
      if (cached) {
        const { text, ts } = JSON.parse(cached);
        if (Date.now() - ts < 60 * 60 * 1000) {
          setRecommendation(text);
          return;
        }
      }
    } catch { /* ignore */ }

    setLoading(true);
    const activeMarkets = cityPins.filter(p => p.city_status === 'active-market').map(p => `${p.city_name}, ${p.state}`);
    const targetMarkets = cityPins.filter(p => p.city_status === 'target-market').map(p => `${p.city_name}, ${p.state}`);
    const prospecting = cityPins.filter(p => p.city_status === 'prospecting').map(p => `${p.city_name}, ${p.state}`);

    getChatCompletion(
      'GEMINI',
      'gemini/gemini-2.5-flash',
      [
        {
          role: 'system',
          content: 'You are a roofing market expansion strategist for NextRoof 360. Based on the current market portfolio, recommend the single best next city to target for expansion. Respond in exactly this format: "Based on your current markets, Gemini recommends targeting [City, State] next — [one sentence reason]." Keep it under 25 words total.',
        },
        {
          role: 'user',
          content: `Current market portfolio:
Active Markets: ${activeMarkets.length > 0 ? activeMarkets.join(', ') : 'None'}
Target Markets: ${targetMarkets.length > 0 ? targetMarkets.join(', ') : 'None'}
Prospecting: ${prospecting.length > 0 ? prospecting.join(', ') : 'None'}

Recommend the best next expansion city for a roofing company.`,
        },
      ],
      { max_tokens: 80, temperature: 0.5 }
    )
      .then((res) => {
        const text = res?.choices?.[0]?.message?.content ?? '';
        setRecommendation(text);
        setLoading(false);
        try {
          sessionStorage.setItem(cacheKey, JSON.stringify({ text, ts: Date.now() }));
        } catch { /* ignore */ }
      })
      .catch(() => {
        setLoading(false);
      });
  }, [cityPins]);

  return (
    <div
      className="flex items-start gap-3 px-4 py-3 rounded-xl"
      style={{ background: 'linear-gradient(135deg, #1C2E5E 0%, #1C2E5E 100%)', border: '1px solid rgba(232,154,60,0.3)' }}
    >
      <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(255,255,255,0.15)' }}>
        <Icon name="SparklesIcon" size={14} className="text-white" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-[10px] font-bold uppercase tracking-wider text-white opacity-70">Gemini AI Recommendation</span>
          {loading && <div className="w-3 h-3 rounded-full border border-[#E89A3C] border-t-transparent animate-spin" />}
        </div>
        {loading ? (
          <div className="space-y-1.5">
            <div className="h-3 rounded-full animate-pulse" style={{ background: 'rgba(255,255,255,0.15)', width: '90%' }} />
            <div className="h-3 rounded-full animate-pulse" style={{ background: 'rgba(255,255,255,0.1)', width: '70%' }} />
          </div>
        ) : recommendation ? (
          <p className="text-sm font-semibold text-white leading-snug">{recommendation}</p>
        ) : (
          <p className="text-sm text-white opacity-60">Add city pins to get AI expansion recommendations</p>
        )}
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
const ExpansionMarketInteractive = () => {
  const { currentUser } = useAuth();
  const [markets, setMarkets] = useState<ExpansionMarket[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCity, setSelectedCity] = useState<ExpansionMarket | null>(null);
  const [reportGenerating, setReportGenerating] = useState(false);
  const supabase = createClient();

  // ─── City Pins state ──────────────────────────────────────────────────────────
  const [cityPins, setCityPins] = useState<CityPin[]>([]);
  const [pinsLoading, setPinsLoading] = useState(true);
  const [clickToAddActive, setClickToAddActive] = useState(false);
  const [pendingCoords, setPendingCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [selectedPin, setSelectedPin] = useState<CityPin | null>(null);
  const [pinSaving, setPinSaving] = useState(false);

  // ─── Quick Add City form state ────────────────────────────────────────────────
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [quickAddCity, setQuickAddCity] = useState('');
  const [quickAddState, setQuickAddState] = useState('');
  const [quickAddStatus, setQuickAddStatus] = useState<CityStatus>('target-market');
  const [quickAddGeocoding, setQuickAddGeocoding] = useState(false);
  const [quickAddError, setQuickAddError] = useState('');

  // ─── Search pins (temporary map pins from Market Search) ─────────────────────
  const [searchPins, setSearchPins] = useState<CityPin[]>([]);

  // ─── Priority list items ──────────────────────────────────────────────────────
  const [priorityItems, setPriorityItems] = useState<ExpansionPriorityItem[]>([]);

  // ─── AI Intelligence state ────────────────────────────────────────────────────
  const [showAiIntelligence, setShowAiIntelligence] = useState(false);
  const [aiScores, setAiScores] = useState<Record<string, number>>({});
  const [aiScoresLoading, setAiScoresLoading] = useState(false);
  const aiScoresFetchedRef = useRef<Set<string>>(new Set());

  // ─── Data Overlay Layers ──────────────────────────────────────────────────────
  const [overlayLayers, setOverlayLayers] = useState<OverlayLayers>({
    hailFrequency: false,
    populationDensity: false,
    medianHomeValue: false,
    competitorDensity: false,
    jobsPerCity: false,
    revenueByTerritory: false,
    leadSourceHeatmap: false,
    crewAssignments: false,
    driveTimeRings: false,
    stormEvents: false,
  });
  const [showOverlayPanel, setShowOverlayPanel] = useState(false);

  // ─── Real overlay data from API routes ────────────────────────────────────────
  const [hailData, setHailData] = useState<HailSpot[]>([]);
  const [censusData, setCensusData] = useState<CensusDataPoint[]>([]);
  const [competitorData, setCompetitorData] = useState<CompetitorLocation[]>([]);
  const [businessData, setBusinessData] = useState<CityBusinessData[]>([]);
  const [crewData, setCrewData] = useState<CrewAssignment[]>([]);
  const [stormPins, setStormPins] = useState<StormPin[]>([]);
  // ─── City Competitor Search state ─────────────────────────────────────────
  const [pendingExternalCompetitor, setPendingExternalCompetitor] = useState<ManualCompetitor | null>(null);
  const [trackedCompetitorIds, setTrackedCompetitorIds] = useState<Set<string>>(new Set());
  const [trackedCompetitors, setTrackedCompetitors] = useState<ManualCompetitor[]>([]);
  const [hotMarkets, setHotMarkets] = useState<{ city: string; state: string; reason: string; lat: number; lng: number }[]>([]);
  const [showHotMarketAlert, setShowHotMarketAlert] = useState(false);
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const overlayFetchedRef = useRef<Set<string>>(new Set());

  // ─── Full-screen state ────────────────────────────────────────────────────────
  const [isFullScreen, setIsFullScreen] = useState(false);
  const mapCardRef = useRef<HTMLDivElement>(null);

  // ─── City pipeline stats ──────────────────────────────────────────────────────
  const [cityPipelineStats, setCityPipelineStats] = useState<CityPipelineStats>({
    jobCount: 0,
    revenue: 0,
    crewCount: 0,
    loading: false,
  });

  const toggleOverlay = useCallback((key: keyof OverlayLayers) => {
    setOverlayLayers((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const activeOverlayCount = Object.values(overlayLayers).filter(Boolean).length;

  // ─── Fetch real overlay data when a layer is first toggled on ─────────────────
  useEffect(() => {
    if (overlayLayers.hailFrequency && !overlayFetchedRef.current.has('hail')) {
      overlayFetchedRef.current.add('hail');
      fetch('/api/expansion/hail-frequency')
        .then((r) => r.json())
        .then((json) => {
          if (json.success && Array.isArray(json.data)) {
            setHailData(json.data);
          }
        })
        .catch(() => { /* silently use static fallback */ });
    }
  }, [overlayLayers.hailFrequency]);

  useEffect(() => {
    const needsCensus = overlayLayers.populationDensity || overlayLayers.medianHomeValue;
    if (needsCensus && !overlayFetchedRef.current.has('census')) {
      overlayFetchedRef.current.add('census');
      fetch('/api/expansion/census-data')
        .then((r) => r.json())
        .then((json) => {
          if (json.success && Array.isArray(json.data)) {
            setCensusData(json.data);
          }
        })
        .catch(() => { /* silently use static fallback */ });
    }
  }, [overlayLayers.populationDensity, overlayLayers.medianHomeValue]);

  useEffect(() => {
    if (overlayLayers.competitorDensity && !overlayFetchedRef.current.has('competitors')) {
      overlayFetchedRef.current.add('competitors');
      fetch('/api/expansion/competitor-density')
        .then((r) => r.json())
        .then((json) => {
          if (json.success && Array.isArray(json.data)) {
            setCompetitorData(json.data);
          }
        })
        .catch(() => { /* silently use static fallback */ });
    }
  }, [overlayLayers.competitorDensity]);

  // ─── Fetch live business data (jobs, revenue, leads) ─────────────────────────
  useEffect(() => {
    const needsBusiness = overlayLayers.jobsPerCity || overlayLayers.revenueByTerritory || overlayLayers.leadSourceHeatmap;
    if (needsBusiness && !overlayFetchedRef.current.has('business')) {
      overlayFetchedRef.current.add('business');
      fetch('/api/expansion/live-business-data')
        .then((r) => r.json())
        .then((json) => {
          if (json.success && Array.isArray(json.data)) {
            setBusinessData(json.data);
          }
        })
        .catch(() => {});
    }
  }, [overlayLayers.jobsPerCity, overlayLayers.revenueByTerritory, overlayLayers.leadSourceHeatmap]);

  // ─── Fetch crew assignments ───────────────────────────────────────────────────
  useEffect(() => {
    if (overlayLayers.crewAssignments && !overlayFetchedRef.current.has('crews')) {
      overlayFetchedRef.current.add('crews');
      fetch('/api/expansion/crew-assignments')
        .then((r) => r.json())
        .then((json) => {
          if (json.success && Array.isArray(json.data)) {
            setCrewData(json.data);
          }
        })
        .catch(() => {});
    }
  }, [overlayLayers.crewAssignments]);

  // ─── Fetch storm events ───────────────────────────────────────────────────────
  useEffect(() => {
    if (overlayLayers.stormEvents && !overlayFetchedRef.current.has('storms')) {
      overlayFetchedRef.current.add('storms');
      fetch('/api/expansion/storm-events')
        .then((r) => r.json())
        .then((json) => {
          if (json.success) {
            if (Array.isArray(json.stormPins)) setStormPins(json.stormPins);
            if (Array.isArray(json.hotMarkets) && json.hotMarkets.length > 0) {
              setHotMarkets(json.hotMarkets);
              setShowHotMarketAlert(true);
            }
          }
        })
        .catch(() => {});
    }
  }, [overlayLayers.stormEvents]);

  // ─── Fetch AI scores for all pins when AI Intelligence is toggled on ──────────
  useEffect(() => {
    if (!showAiIntelligence || cityPins.length === 0) return;

    const pinsNeedingScores = cityPins.filter(p => !aiScoresFetchedRef.current.has(p.id));
    if (pinsNeedingScores.length === 0) return;

    setAiScoresLoading(true);

    // Fetch all scores using batch helper (concurrency=3, per-pin caching)
    fetchAiScoresBatch(pinsNeedingScores, 3)
      .then((newScores) => {
        pinsNeedingScores.forEach((pin) => {
          if (newScores[pin.id] != null) aiScoresFetchedRef.current.add(pin.id);
        });
        setAiScores((prev) => ({ ...prev, ...newScores }));
        setAiScoresLoading(false);
      })
      .catch(() => setAiScoresLoading(false));
  }, [showAiIntelligence, cityPins]);

  // ─── Fetch markets ──────────────────────────────────────────────────────────
  useEffect(() => {
    const fetchMarkets = async () => {
      try {
        const { data, error } = await supabase
          .from('expansion_markets')
          .select('*')
          .order('viability_score', { ascending: false });

        if (error) {
          console.log('Failed to load markets:', error.message);
          setMarkets([]);
        } else if (data && data.length > 0) {
          setMarkets(data as ExpansionMarket[]);
        } else {
          setMarkets([]);
        }
      } catch {
        setMarkets([]);
      } finally {
        setLoading(false);
      }
    };

    fetchMarkets();

    const channel = supabase
      .channel('expansion_markets_realtime')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'expansion_markets' },
        (payload) => {
          const newMarket = payload.new as ExpansionMarket;
          setMarkets((prev) => {
            // Insert in sorted order by viability_score descending
            const updated = [...prev, newMarket];
            return updated.sort((a, b) => b.viability_score - a.viability_score);
          });
        }
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'expansion_markets' },
        (payload) => {
          const updatedMarket = payload.new as ExpansionMarket;
          setMarkets((prev) => {
            const updated = prev.map((m) =>
              m.id === updatedMarket.id ? updatedMarket : m
            );
            return updated.sort((a, b) => b.viability_score - a.viability_score);
          });
          setSelectedCity((prev) =>
            prev?.id === updatedMarket.id ? updatedMarket : prev
          );
        }
      )
      .on(
        'postgres_changes',
        { event: 'DELETE', schema: 'public', table: 'expansion_markets' },
        (payload) => {
          const deletedId = (payload.old as { id: string }).id;
          setMarkets((prev) => prev.filter((m) => m.id !== deletedId));
          setSelectedCity((prev) => (prev?.id === deletedId ? null : prev));
        }
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, []);

  // ─── Fetch city pins ────────────────────────────────────────────────────────
  useEffect(() => {
    const fetchPins = async () => {
      try {
        const { data, error } = await supabase
          .from('city_tiers')
          .select('id, city_name, state, latitude, longitude, city_status, radius_rings, tier')
          .order('created_at', { ascending: false });

        if (error) {
          console.log('Failed to load city pins:', error.message);
          setCityPins([]);
        } else if (data) {
          setCityPins(data.map((row: any) => ({
            id: row.id,
            city_name: row.city_name,
            state: row.state,
            latitude: row.latitude,
            longitude: row.longitude,
            city_status: (row.city_status as CityStatus) ?? 'target-market',
            radius_rings: (row.radius_rings as RadiusRing[]) ?? [],
            tier: row.tier,
          })));
        }
      } catch {
        setCityPins([]);
      } finally {
        setPinsLoading(false);
      }
    };

    fetchPins();

    const channel = supabase
      .channel('city_tiers_realtime')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'city_tiers' },
        (payload) => {
          const row = payload.new as any;
          const newPin: CityPin = {
            id: row.id,
            city_name: row.city_name,
            state: row.state,
            latitude: row.latitude,
            longitude: row.longitude,
            city_status: (row.city_status as CityStatus) ?? 'target-market',
            radius_rings: (row.radius_rings as RadiusRing[]) ?? [],
            tier: row.tier,
          };
          setCityPins((prev) => {
            // Avoid duplicates (local optimistic update may have already added it)
            if (prev.some((p) => p.id === newPin.id)) return prev;
            return [newPin, ...prev];
          });
        }
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'city_tiers' },
        (payload) => {
          const row = payload.new as any;
          const updatedPin: CityPin = {
            id: row.id,
            city_name: row.city_name,
            state: row.state,
            latitude: row.latitude,
            longitude: row.longitude,
            city_status: (row.city_status as CityStatus) ?? 'target-market',
            radius_rings: (row.radius_rings as RadiusRing[]) ?? [],
            tier: row.tier,
          };
          setCityPins((prev) =>
            prev.map((p) => (p.id === updatedPin.id ? updatedPin : p))
          );
          setSelectedPin((prev) =>
            prev?.id === updatedPin.id ? updatedPin : prev
          );
        }
      )
      .on(
        'postgres_changes',
        { event: 'DELETE', schema: 'public', table: 'city_tiers' },
        (payload) => {
          const deletedId = (payload.old as { id: string }).id;
          setCityPins((prev) => prev.filter((p) => p.id !== deletedId));
          setSelectedPin((prev) => (prev?.id === deletedId ? null : prev));
        }
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, []);

  // ─── Fetch pipeline stats for selected pin ──────────────────────────────────
  useEffect(() => {
    if (!selectedPin) return;

    const fetchStats = async () => {
      setCityPipelineStats({ jobCount: 0, revenue: 0, crewCount: 0, loading: true });
      try {
        const cityName = selectedPin.city_name;

        const { data: pipelineData, error: pipelineError } = await supabase
          .from('customer_pipeline')
          .select('contract_value')
          .ilike('city', cityName);

        const { data: crewData, error: crewError } = await supabase
          .from('installer_crews')
          .select('id')
          .ilike('city', cityName);

        const jobCount = pipelineError ? 0 : (pipelineData?.length ?? 0);
        const revenue = pipelineError
          ? 0
          : (pipelineData ?? []).reduce((sum: number, row: any) => sum + (Number(row.contract_value) || 0), 0);
        const crewCount = crewError ? 0 : (crewData?.length ?? 0);

        setCityPipelineStats({ jobCount, revenue, crewCount, loading: false });
      } catch {
        setCityPipelineStats({ jobCount: 0, revenue: 0, crewCount: 0, loading: false });
      }
    };

    fetchStats();
  }, [selectedPin, supabase]);

  // ─── Handlers ───────────────────────────────────────────────────────────────
  const handleMapClick = useCallback((lat: number, lng: number) => {
    setPendingCoords({ lat, lng });
  }, []);

  const handleAddPin = useCallback(async (pin: CityPin) => {
    setPinSaving(true);
    try {
      const { data, error } = await supabase
        .from('city_tiers')
        .insert({
          city_name: pin.city_name,
          state: pin.state,
          latitude: pin.latitude,
          longitude: pin.longitude,
          city_status: pin.city_status,
          radius_rings: pin.radius_rings,
          tier: pin.city_status === 'active-market' ? 'active' : pin.city_status === 'target-market' ? 'coming-soon' : 'next-12-months',
        })
        .select()
        .single();

      if (error) throw error;

      const newPin: CityPin = {
        id: data.id,
        city_name: data.city_name,
        state: data.state,
        latitude: data.latitude,
        longitude: data.longitude,
        city_status: data.city_status ?? 'target-market',
        radius_rings: data.radius_rings ?? [],
        tier: data.tier,
      };
      setCityPins((prev) => [newPin, ...prev]);
      setPendingCoords(null);
      setClickToAddActive(false);
    } catch (err) {
      console.error('Failed to save pin:', err);
    } finally {
      setPinSaving(false);
    }
  }, [supabase]);

  // ─── Quick Add City: geocode then save ────────────────────────────────────────
  const handleQuickAddCity = useCallback(async () => {
    if (!quickAddCity.trim() || !quickAddState.trim()) return;
    setQuickAddGeocoding(true);
    setQuickAddError('');
    try {
      const geoRes = await fetch('/api/geocode-address', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ address: `${quickAddCity.trim()}, ${quickAddState.trim().toUpperCase().slice(0, 2)}` }),
      });
      const geoData = await geoRes.json();
      if (!geoData.success || !geoData.lat || !geoData.lng) {
        setQuickAddError('Could not find that city. Check spelling and try again.');
        setQuickAddGeocoding(false);
        return;
      }
      const stateCode = quickAddState.trim().toUpperCase().slice(0, 2);
      const { data, error } = await supabase
        .from('city_tiers')
        .insert({
          city_name: quickAddCity.trim(),
          state: stateCode,
          latitude: geoData.lat,
          longitude: geoData.lng,
          city_status: quickAddStatus,
          radius_rings: [],
          tier: STATUS_CONFIG[quickAddStatus].bg,
        })
        .select()
        .single();
      if (error) throw error;
      const newPin: CityPin = {
        id: data.id,
        city_name: data.city_name,
        state: data.state,
        latitude: data.latitude,
        longitude: data.longitude,
        city_status: data.city_status ?? quickAddStatus,
        radius_rings: data.radius_rings ?? [],
        tier: data.tier,
      };
      setCityPins((prev) => [newPin, ...prev]);
      // Reset form
      setQuickAddCity('');
      setQuickAddState('');
      setQuickAddStatus('target-market');
      setQuickAddOpen(false);
      // Select the new pin so map flies to it
      setSelectedPin(newPin);
    } catch (err) {
      console.error('Quick add failed:', err);
      setQuickAddError('Failed to save pin. Please try again.');
    } finally {
      setQuickAddGeocoding(false);
    }
  }, [quickAddCity, quickAddState, quickAddStatus, supabase]);

  const handleStatusChange = useCallback(async (id: string, status: CityStatus) => {
    setPinSaving(true);
    try {
      const { error } = await supabase
        .from('city_tiers')
        .update({ city_status: status, tier: STATUS_CONFIG[status].bg })
        .eq('id', id);
      if (error) throw error;
      setCityPins((prev) => prev.map((p) => p.id === id ? { ...p, city_status: status } : p));
      setSelectedPin((prev) => prev?.id === id ? { ...prev, city_status: status } : prev);
    } catch (err) {
      console.error('Failed to update status:', err);
    } finally {
      setPinSaving(false);
    }
  }, [supabase]);

  const handleRingToggle = useCallback(async (id: string, ring: RadiusRing) => {
    const pin = cityPins.find((p) => p.id === id);
    if (!pin) return;
    const currentRings = pin.radius_rings ?? [];
    const newRings: RadiusRing[] = currentRings.includes(ring)
      ? currentRings.filter((r) => r !== ring)
      : [...currentRings, ring];

    setPinSaving(true);
    try {
      const { error } = await supabase
        .from('city_tiers')
        .update({ radius_rings: newRings })
        .eq('id', id);
      if (error) throw error;
      setCityPins((prev) => prev.map((p) => p.id === id ? { ...p, radius_rings: newRings } : p));
      setSelectedPin((prev) => prev?.id === id ? { ...prev, radius_rings: newRings } : prev);
    } catch (err) {
      console.error('Failed to update rings:', err);
    } finally {
      setPinSaving(false);
    }
  }, [supabase, cityPins]);

  const handleDeletePin = useCallback(async (id: string) => {
    setPinSaving(true);
    try {
      const { error } = await supabase.from('city_tiers').delete().eq('id', id);
      if (error) throw error;
      setCityPins((prev) => prev.filter((p) => p.id !== id));
      setSelectedPin(null);
    } catch (err) {
      console.error('Failed to delete pin:', err);
    } finally {
      setPinSaving(false);
    }
  }, [supabase]);

  // ─── Save searched market to priority list ────────────────────────────────────
  const handleSaveSearchedMarket = useCallback((market: SearchedMarket) => {
    setPriorityItems((prev) => {
      const alreadyExists = prev.some(
        (item) => item.city.toLowerCase() === market.city.toLowerCase() && item.state === market.state
      );
      if (alreadyExists) return prev;
      const newItem: ExpansionPriorityItem = {
        id: crypto.randomUUID ? crypto.randomUUID() : `search-saved-${market.id}-${Date.now()}`,
        rank: prev.length + 1,
        city: market.city,
        state: market.state,
        stage: 'Researching',
        owner: 'Unassigned',
        targetQuarter: 'Q3 2026',
        aiJustification: market.marketBrief ? market.marketBrief.split('.')[0] + '.' : '',
        justificationLoading: false,
        notes: `AI Score: ${market.aiScore}/100 · ${market.stormHistory}`,
      };
      // Persist to DB so it survives page reload
      const supabase = createClient();
      supabase.from('expansion_priority_list').upsert({
        id: newItem.id,
        rank: newItem.rank,
        city: newItem.city,
        state: newItem.state,
        stage: newItem.stage,
        owner: newItem.owner,
        target_quarter: newItem.targetQuarter,
        ai_justification: newItem.aiJustification,
        notes: newItem.notes,
        updated_at: new Date().toISOString(),
      }, { onConflict: 'id' }).then(({ error }: { error: any }) => {
        if (error) console.error('Failed to save market to DB:', error);
      });
      return [...prev, newItem];
    });
  }, []);

  const activeMarkets = markets.filter((m) => m.is_active_market);

  const mapStates: { stateName: string; color: string; isActive: boolean; isRecommended: boolean; viabilityScore: number; city: string }[] = [];

  const handleGenerateReport = () => {
    setReportGenerating(true);
    setTimeout(() => setReportGenerating(false), 2500);
  };

  // ─── PDF Territory Report Export ─────────────────────────────────────────────
  const handleExportPDF = useCallback(() => {
    setPdfGenerating(true);

    // Build report content as a printable HTML page
    const reportDate = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    const activeCount = cityPins.filter((p) => p.city_status === 'active-market').length;
    const targetCount = cityPins.filter((p) => p.city_status === 'target-market').length;
    const totalRevenue = businessData.reduce((sum, d) => sum + d.revenue, 0);
    const totalJobs = businessData.reduce((sum, d) => sum + d.jobCount, 0);

    const cityRows = cityPins.map((pin) => {
      const biz = businessData.find((d) => d.city.toLowerCase() === pin.city_name.toLowerCase());
      const cfg = STATUS_CONFIG[pin.city_status] ?? STATUS_CONFIG['target-market'];
      const aiScore = aiScores[pin.id];
      return `
        <tr style="border-bottom:1px solid #E5E7EB;">
          <td style="padding:8px 12px;font-weight:600;">${pin.city_name}, ${pin.state}</td>
          <td style="padding:8px 12px;color:${cfg.color};font-weight:600;">${cfg.label}</td>
          <td style="padding:8px 12px;">${biz ? biz.jobCount : '—'}</td>
          <td style="padding:8px 12px;">${biz ? '$' + (biz.revenue / 1000).toFixed(0) + 'K' : '—'}</td>
          <td style="padding:8px 12px;">${aiScore != null ? aiScore + '/100' : '—'}</td>
        </tr>`;
    }).join('');

    const hotMarketRows = hotMarkets.map((hm) => `
      <tr style="border-bottom:1px solid #FEE2E2;">
        <td style="padding:8px 12px;font-weight:600;color:#EF4444;">⛈ ${hm.city}, ${hm.state}</td>
        <td style="padding:8px 12px;color:#6B7280;">${hm.reason}</td>
      </tr>`).join('');

    const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>NextRoof 360 Territory Report — ${reportDate}</title>
  <style>
    body { font-family: Inter, Arial, sans-serif; color: #111; margin: 0; padding: 32px; }
    h1 { color: #1C2E5E; font-size: 22px; margin-bottom: 4px; }
    h2 { color: #1C2E5E; font-size: 15px; margin: 24px 0 8px; border-bottom: 2px solid #E5E7EB; padding-bottom: 6px; }
    .meta { color: #6B7280; font-size: 12px; margin-bottom: 24px; }
    .kpi-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; margin-bottom: 24px; }
    .kpi { background: #F9FAFB; border: 1px solid #E5E7EB; border-radius: 10px; padding: 14px; }
    .kpi-value { font-size: 24px; font-weight: 900; color: #1C2E5E; }
    .kpi-label { font-size: 11px; color: #6B7280; margin-top: 4px; }
    table { width: 100%; border-collapse: collapse; font-size: 12px; }
    th { background: #F3F4F6; padding: 8px 12px; text-align: left; font-size: 11px; color: #6B7280; text-transform: uppercase; letter-spacing: 0.05em; }
    .footer { margin-top: 32px; padding-top: 16px; border-top: 1px solid #E5E7EB; font-size: 11px; color: #9CA3AF; }
  </style>
</head>
<body>
  <h1>🗺️ NextRoof 360 Territory Report</h1>
  <div class="meta">Generated: ${reportDate} · Expansion Market Intelligence Dashboard</div>

  <div class="kpi-grid">
    <div class="kpi"><div class="kpi-value">${cityPins.length}</div><div class="kpi-label">Total City Pins</div></div>
    <div class="kpi"><div class="kpi-value">${activeCount}</div><div class="kpi-label">Active Markets</div></div>
    <div class="kpi"><div class="kpi-value">${totalJobs}</div><div class="kpi-label">Pipeline Jobs</div></div>
    <div class="kpi"><div class="kpi-value">$${(totalRevenue / 1000).toFixed(0)}K</div><div class="kpi-label">Total Revenue</div></div>
  </div>

  <h2>📍 Market Status Overview</h2>
  <table>
    <thead><tr><th>City</th><th>Status</th><th>Jobs</th><th>Revenue</th><th>AI Score</th></tr></thead>
    <tbody>${cityRows || '<tr><td colspan="5" style="padding:12px;color:#9CA3AF;text-align:center;">No city pins added yet</td></tr>'}</tbody>
  </table>

  ${hotMarkets.length > 0 ? `
  <h2>🔔 Hot Market Alerts</h2>
  <table>
    <thead><tr><th>Market</th><th>Alert Reason</th></tr></thead>
    <tbody>${hotMarketRows}</tbody>
  </table>` : ''}

  <div class="footer">
    NextRoof 360 Expansion Intelligence · Confidential · For Leadership Review Only
  </div>
</body>
</html>`;

    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const win = window.open(url, '_blank');
    if (win) {
      win.onload = () => {
        win.print();
        URL.revokeObjectURL(url);
      };
    }
    setPdfGenerating(false);
  }, [cityPins, businessData, hotMarkets, aiScores]);

  const handleDeepDive = (market: ExpansionMarket) => {
    setSelectedCity((prev) => (prev?.id === market.id ? null : market));
    setTimeout(() => {
      document.getElementById('deep-dive-panel')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  // Pin counts by status
  const pinCounts = cityPins.reduce((acc, p) => {
    acc[p.city_status] = (acc[p.city_status] ?? 0) + 1;
    return acc;
  }, {} as Record<CityStatus, number>);

  // ─── Overlay config ───────────────────────────────────────────────────────────
  const OVERLAY_CONFIG = [
    {
      key: 'hailFrequency' as keyof OverlayLayers,
      label: 'Hail Frequency',
      description: 'NOAA storm data — hail-prone zip codes',
      icon: 'CloudIcon',
      color: '#EF4444',
      bg: '#FEE2E2',
      activeBg: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
      group: 'Market Data',
    },
    {
      key: 'populationDensity' as keyof OverlayLayers,
      label: 'Population Density',
      description: 'High-density suburban corridors',
      icon: 'UserGroupIcon',
      color: '#7C3AED',
      bg: '#EDE9FE',
      activeBg: 'linear-gradient(135deg, #7C3AED 0%, #6D28D9 100%)',
      group: 'Market Data',
    },
    {
      key: 'medianHomeValue' as keyof OverlayLayers,
      label: 'Median Home Value',
      description: 'Premium roofing market zones',
      icon: 'HomeIcon',
      color: '#D97706',
      bg: '#FEF3C7',
      activeBg: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
      group: 'Market Data',
    },
    {
      key: 'competitorDensity' as keyof OverlayLayers,
      label: 'Competitor Density',
      description: 'Known competitor locations & gaps',
      icon: 'FlagIcon',
      color: '#DC2626',
      bg: '#FEE2E2',
      activeBg: 'linear-gradient(135deg, #DC2626 0%, #B91C1C 100%)',
      group: 'Market Data',
    },
    {
      key: 'jobsPerCity' as keyof OverlayLayers,
      label: 'Jobs Per City',
      description: 'Live job count from customer pipeline',
      icon: 'BriefcaseIcon',
      color: '#10B981',
      bg: '#ECFDF5',
      activeBg: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
      group: 'Live Business',
    },
    {
      key: 'revenueByTerritory' as keyof OverlayLayers,
      label: 'Revenue by Territory',
      description: 'Bubble size scales with revenue generated',
      icon: 'CurrencyDollarIcon',
      color: '#8B5CF6',
      bg: '#EDE9FE',
      activeBg: 'linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)',
      group: 'Live Business',
    },
    {
      key: 'leadSourceHeatmap' as keyof OverlayLayers,
      label: 'Lead Source Heatmap',
      description: 'Where Google Ads leads come from',
      icon: 'MagnifyingGlassIcon',
      color: '#F59E0B',
      bg: '#FEF3C7',
      activeBg: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
      group: 'Live Business',
    },
    {
      key: 'crewAssignments' as keyof OverlayLayers,
      label: 'Crew Assignments',
      description: 'Which crews cover which territories',
      icon: 'WrenchScrewdriverIcon',
      color: '#0EA5E9',
      bg: '#E0F2FE',
      activeBg: 'linear-gradient(135deg, #0EA5E9 0%, #0284C7 100%)',
      group: 'Operational',
    },
    {
      key: 'driveTimeRings' as keyof OverlayLayers,
      label: 'Drive Time Rings',
      description: '30/60/90 min from Dayton HQ',
      icon: 'ClockIcon',
      color: '#6366F1',
      bg: '#EEF2FF',
      activeBg: 'linear-gradient(135deg, #6366F1 0%, #4338CA 100%)',
      group: 'Operational',
    },
    {
      key: 'stormEvents' as keyof OverlayLayers,
      label: 'Storm Event Pins',
      description: 'Real-time weather events & hot markets',
      icon: 'BoltIcon',
      color: '#EF4444',
      bg: '#FEE2E2',
      activeBg: 'linear-gradient(135deg, #EF4444 0%, #B91C1C 100%)',
      group: 'Alerts',
    },
  ];

  return (
    <div className="min-h-screen" style={{ background: '#F8F9FA' }}>

      {/* ── Sticky Header ──────────────────────────────────────────────────── */}
      <div
        className="sticky top-[60px] z-30 flex items-center justify-between px-6 py-4 border-b"
        style={{ background: '#fff', borderColor: '#E5E7EB', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}
      >
        <div>
          <h1 className="text-xl font-bold" style={{ color: '#1C2E5E', fontFamily: 'Inter, Poppins, sans-serif' }}>
            Market Expansion Intelligence
          </h1>
          <p className="text-xs mt-0.5" style={{ color: '#6B7280' }}>
            AI-powered strategic market analysis · CEO &amp; Expansion Team
          </p>
        </div>

        {/* ── Toolbar buttons row ── */}
        <div className="flex items-center gap-2">
          {/* Active Markets badge */}
          <span className="text-xs px-3 py-1 rounded-full font-semibold" style={{ background: '#EFF6FF', color: '#E89A3C', border: '1.5px solid #BFDBFE' }}>
            {pinCounts['active-market'] ?? 0} Active Markets
          </span>

          {/* Pins count */}
          <span className="text-xs px-3 py-1 rounded-full font-semibold" style={{ background: '#F3F4F6', color: '#374151', border: '1.5px solid #E5E7EB' }}>
            {cityPins.length} Pins
          </span>

          {/* Data Overlays button */}
          <button
            onClick={() => setShowOverlayPanel((v) => !v)}
            className="flex items-center gap-2 px-3 py-1.5 rounded-xl text-xs font-semibold transition-all"
            style={{
              background: showOverlayPanel || activeOverlayCount > 0 ? '#EFF6FF' : '#F3F4F6',
              color: showOverlayPanel || activeOverlayCount > 0 ? '#E89A3C' : '#374151',
              border: `1.5px solid ${showOverlayPanel || activeOverlayCount > 0 ? '#E89A3C' : '#E5E7EB'}`,
            }}
          >
            <Icon name="ChartBarIcon" size={13} />
            Data Overlays
            {activeOverlayCount > 0 && (
              <span className="ml-1 w-4 h-4 rounded-full text-[10px] font-bold flex items-center justify-center" style={{ background: '#E89A3C', color: '#fff' }}>
                {activeOverlayCount}
              </span>
            )}
          </button>

          {/* AI Intelligence Toggle */}
          <button
            onClick={() => setShowAiIntelligence((v) => !v)}
            className="flex items-center gap-2 px-3 py-1.5 rounded-xl text-xs font-semibold transition-all"
            style={{
              background: showAiIntelligence
                ? 'linear-gradient(135deg, #E89A3C 0%, #1C2E5E 100%)'
                : '#F3F4F6',
              color: showAiIntelligence ? '#fff' : '#374151',
              border: `1.5px solid ${showAiIntelligence ? '#E89A3C' : '#E5E7EB'}`,
              boxShadow: showAiIntelligence ? '0 2px 8px rgba(232,154,60,0.35)' : 'none',
            }}
          >
            <Icon name="SparklesIcon" size={13} />
            AI Intelligence
            {aiScoresLoading && showAiIntelligence && (
              <div className="w-3 h-3 rounded-full border-2 border-white border-t-transparent animate-spin" />
            )}
          </button>

          {/* Add City button */}
          <button
            onClick={() => {
              setQuickAddOpen((v) => !v);
              setQuickAddError('');
              if (clickToAddActive) {
                setClickToAddActive(false);
                setPendingCoords(null);
              }
            }}
            className="flex items-center gap-2 px-3 py-1.5 rounded-xl text-xs font-semibold transition-all"
            style={{
              background: quickAddOpen ? '#1C2E5E' : '#F3F4F6',
              color: quickAddOpen ? '#fff' : '#374151',
              border: `1.5px solid ${quickAddOpen ? '#1C2E5E' : '#E5E7EB'}`,
            }}
          >
            <Icon name="MapPinIcon" size={13} />
            {quickAddOpen ? 'Cancel' : 'Add City'}
          </button>

          {/* Fullscreen toggle */}
          <button
            onClick={() => setIsFullScreen((v) => !v)}
            className="w-8 h-8 rounded-xl flex items-center justify-center transition-all hover:bg-gray-100"
            style={{ border: '1.5px solid #E5E7EB', color: '#374151' }}
            title={isFullScreen ? 'Exit Full Screen' : 'Enter Full Screen'}
          >
            <Icon name={isFullScreen ? 'ArrowsPointingInIcon' : 'ArrowsPointingOutIcon'} size={15} />
          </button>
        </div>
      </div>

      <div className="p-6 space-y-6">

        {/* ─── Legend ─────────────────────────────────────────────────────────── */}
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-sm" style={{ background: '#E89A3C' }} />
            <span className="text-[10px] font-medium" style={{ color: '#374151' }}>Active Markets (Ohio, Texas)</span>
          </div>
          <div className="w-px h-4" style={{ background: '#E5E7EB' }} />
          {(Object.entries(STATUS_CONFIG) as [CityStatus, typeof STATUS_CONFIG[CityStatus]][]).map(([key, cfg]) => (
            <div key={key} className="flex items-center gap-1.5">
              <span className="text-sm">{cfg.emoji}</span>
              <span className="text-xs font-semibold" style={{ color: '#374151' }}>{cfg.label}</span>
              {(pinCounts[key] ?? 0) > 0 && (
                <span className="text-xs font-bold px-2 py-1 rounded-full" style={{ background: cfg.bg ?? '#F3F4F6', color: cfg.color }}>
                  {pinCounts[key]}
                </span>
              )}
            </div>
          ))}
        </div>

        {/* ─── Hero Map Card ───────────────────────────────────────────────────── */}
        <div
          ref={mapCardRef}
          className={`rounded-2xl overflow-hidden transition-all ${isFullScreen ? 'fixed inset-0 z-[9990] rounded-none' : ''}`}
          style={{
            background: '#fff',
            boxShadow: isFullScreen ? 'none' : '0 2px 16px rgba(0,0,0,0.07)',
            border: isFullScreen ? 'none' : '1px solid #E5E7EB',
          }}
        >
          <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: '#F3F4F6' }}>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #E89A3C 0%, #1C2E5E 100%)' }}>
                <Icon name="GlobeAmericasIcon" size={18} className="text-white" />
              </div>
              <div>
                <h2 className="text-base font-bold" style={{ color: '#1C2E5E' }}>US Expansion Map</h2>
                <p className="text-xs" style={{ color: '#9CA3AF' }}>
                  {clickToAddActive ? '📍 Click anywhere on the map to drop a city pin' : 'Click a pin to manage status & territory rings'}
                </p>
              </div>
            </div>

            {/* ── Toolbar buttons row ── */}
            <div className="flex items-center gap-2">
              {/* Active Markets badge */}
              <span className="text-xs px-3 py-1 rounded-full font-semibold" style={{ background: '#EFF6FF', color: '#E89A3C', border: '1.5px solid #BFDBFE' }}>
                {pinCounts['active-market'] ?? 0} Active Markets
              </span>

              {/* Pins count */}
              <span className="text-xs px-3 py-1 rounded-full font-semibold" style={{ background: '#F3F4F6', color: '#374151', border: '1.5px solid #E5E7EB' }}>
                {cityPins.length} Pins
              </span>

              {/* Data Overlays button */}
              <button
                onClick={() => setShowOverlayPanel((v) => !v)}
                className="flex items-center gap-2 px-3 py-1.5 rounded-xl text-xs font-semibold transition-all"
                style={{
                  background: showOverlayPanel || activeOverlayCount > 0 ? '#EFF6FF' : '#F3F4F6',
                  color: showOverlayPanel || activeOverlayCount > 0 ? '#E89A3C' : '#374151',
                  border: `1.5px solid ${showOverlayPanel || activeOverlayCount > 0 ? '#E89A3C' : '#E5E7EB'}`,
                }}
              >
                <Icon name="ChartBarIcon" size={13} />
                Data Overlays
                {activeOverlayCount > 0 && (
                  <span className="ml-1 w-4 h-4 rounded-full text-[10px] font-bold flex items-center justify-center" style={{ background: '#E89A3C', color: '#fff' }}>
                    {activeOverlayCount}
                  </span>
                )}
              </button>

              {/* AI Intelligence Toggle */}
              <button
                onClick={() => setShowAiIntelligence((v) => !v)}
                className="flex items-center gap-2 px-3 py-1.5 rounded-xl text-xs font-semibold transition-all"
                style={{
                  background: showAiIntelligence
                    ? 'linear-gradient(135deg, #E89A3C 0%, #1C2E5E 100%)'
                    : '#F3F4F6',
                  color: showAiIntelligence ? '#fff' : '#374151',
                  border: `1.5px solid ${showAiIntelligence ? '#E89A3C' : '#E5E7EB'}`,
                  boxShadow: showAiIntelligence ? '0 2px 8px rgba(232,154,60,0.35)' : 'none',
                }}
              >
                <Icon name="SparklesIcon" size={13} />
                AI Intelligence
                {aiScoresLoading && showAiIntelligence && (
                  <div className="w-3 h-3 rounded-full border-2 border-white border-t-transparent animate-spin" />
                )}
              </button>

              {/* Add City button */}
              <button
                onClick={() => {
                  setQuickAddOpen((v) => !v);
                  setQuickAddError('');
                  if (clickToAddActive) {
                    setClickToAddActive(false);
                    setPendingCoords(null);
                  }
                }}
                className="flex items-center gap-2 px-3 py-1.5 rounded-xl text-xs font-semibold transition-all"
                style={{
                  background: quickAddOpen ? '#1C2E5E' : '#F3F4F6',
                  color: quickAddOpen ? '#fff' : '#374151',
                  border: `1.5px solid ${quickAddOpen ? '#1C2E5E' : '#E5E7EB'}`,
                }}
              >
                <Icon name="MapPinIcon" size={13} />
                {quickAddOpen ? 'Cancel' : 'Add City'}
              </button>

              {/* Fullscreen toggle */}
              <button
                onClick={() => setIsFullScreen((v) => !v)}
                className="w-8 h-8 rounded-xl flex items-center justify-center transition-all hover:bg-gray-100"
                style={{ border: '1.5px solid #E5E7EB', color: '#374151' }}
                title={isFullScreen ? 'Exit Full Screen' : 'Enter Full Screen'}
              >
                <Icon name={isFullScreen ? 'ArrowsPointingInIcon' : 'ArrowsPointingOutIcon'} size={15} />
              </button>
            </div>
          </div>

          {/* ── Quick Add City Form ── */}
          {quickAddOpen && (
            <div className="mx-6 mb-3 rounded-2xl overflow-hidden" style={{ border: '1.5px solid #E5E7EB', background: '#F9FAFB' }}>
              <div className="px-4 py-3 flex items-center gap-2" style={{ background: 'linear-gradient(135deg, #1C2E5E 0%, #1C2E5E 100%)' }}>
                <div className="w-6 h-6 rounded-lg flex items-center justify-center" style={{ background: 'rgba(232,154,60,0.25)' }}>
                  <Icon name="MapPinIcon" size={13} className="text-white" />
                </div>
                <span className="text-sm font-bold text-white">Quick Add City</span>
                <span className="text-xs ml-1" style={{ color: 'rgba(255,255,255,0.55)' }}>— type a city &amp; state, pick status, pin drops automatically</span>
              </div>
              <div className="p-4">
                <div className="flex flex-wrap gap-3 items-end">
                  {/* City input */}
                  <div className="flex-1 min-w-[140px]">
                    <label className="block text-xs font-semibold mb-1" style={{ color: '#374151' }}>City *</label>
                    <input
                      type="text"
                      value={quickAddCity}
                      onChange={(e) => { setQuickAddCity(e.target.value); setQuickAddError(''); }}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleQuickAddCity(); }}
                      placeholder="e.g. Cincinnati"
                      className="w-full px-3 py-2 rounded-xl text-sm border outline-none focus:ring-2 focus:ring-blue-300"
                      style={{ borderColor: '#E5E7EB', color: '#1C2E5E', background: '#fff' }}
                      autoFocus
                    />
                  </div>
                  {/* State input */}
                  <div style={{ width: '72px' }}>
                    <label className="block text-xs font-semibold mb-1" style={{ color: '#374151' }}>State *</label>
                    <input
                      type="text"
                      value={quickAddState}
                      onChange={(e) => { setQuickAddState(e.target.value.toUpperCase().slice(0, 2)); setQuickAddError(''); }}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleQuickAddCity(); }}
                      placeholder="OH"
                      maxLength={2}
                      className="w-full px-3 py-2 rounded-xl text-sm border outline-none focus:ring-2 focus:ring-blue-300 uppercase text-center font-bold"
                      style={{ borderColor: '#E5E7EB', color: '#1C2E5E', background: '#fff' }}
                    />
                  </div>
                  {/* Status select */}
                  <div className="flex-1 min-w-[160px]">
                    <label className="block text-xs font-semibold mb-1" style={{ color: '#374151' }}>Status</label>
                    <select
                      value={quickAddStatus}
                      onChange={(e) => setQuickAddStatus(e.target.value as CityStatus)}
                      className="w-full px-3 py-2 rounded-xl text-sm border outline-none focus:ring-2 focus:ring-blue-300"
                      style={{ borderColor: '#E5E7EB', color: '#1C2E5E', background: '#fff' }}
                    >
                      {(Object.entries(STATUS_CONFIG) as [CityStatus, typeof STATUS_CONFIG[CityStatus]][]).map(([key, cfg]) => (
                        <option key={key} value={key}>{cfg.emoji} {cfg.label}</option>
                      ))}
                    </select>
                  </div>
                  {/* Submit button */}
                  <button
                    onClick={handleQuickAddCity}
                    disabled={!quickAddCity.trim() || !quickAddState.trim() || quickAddGeocoding}
                    className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90 disabled:opacity-50"
                    style={{ background: 'linear-gradient(135deg, #E89A3C 0%, #1C2E5E 100%)', whiteSpace: 'nowrap' }}
                  >
                    {quickAddGeocoding ? (
                      <div className="w-4 h-4 rounded-full border-2 border-white border-t-transparent animate-spin" />
                    ) : (
                      <Icon name="MapPinIcon" size={14} />
                    )}
                    {quickAddGeocoding ? 'Placing pin…' : 'Drop Pin'}
                  </button>
                </div>
                {quickAddError && (
                  <p className="mt-2 text-xs font-medium flex items-center gap-1.5" style={{ color: '#EF4444' }}>
                    <Icon name="ExclamationCircleIcon" size={13} />
                    {quickAddError}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* ── AI Recommendation Banner (shown when AI Intelligence is on) ── */}
          {showAiIntelligence && cityPins.length > 0 && (
            <div className="px-6 pt-3 pb-2">
              <AIRecommendationBanner cityPins={cityPins} />
            </div>
          )}

          {/* ── Active Overlay Legend ── */}
          {activeOverlayCount > 0 && (
            <div className="px-6 pt-3 pb-1 flex items-center gap-3 flex-wrap">
              <span className="text-[10px] font-bold uppercase tracking-wide" style={{ color: '#9CA3AF' }}>Active Overlays:</span>
              {OVERLAY_CONFIG.filter((o) => overlayLayers[o.key]).map((o) => (
                <div key={o.key} className="flex items-center gap-1.5 px-2.5 py-1 rounded-full" style={{ background: o.bg, border: `1px solid ${o.color}` }}>
                  <Icon name={o.icon as any} size={10} style={{ color: o.color }} />
                  <span className="text-[10px] font-semibold" style={{ color: o.color }}>{o.label}</span>
                  <button
                    onClick={() => toggleOverlay(o.key)}
                    className="ml-0.5 hover:opacity-70"
                    style={{ color: o.color }}
                  >
                    <Icon name="XMarkIcon" size={9} />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Map + Side Panel */}
          <div className="flex" style={{ height: isFullScreen ? 'calc(100vh - 130px)' : '460px' }}>
            {/* Map */}
            <div className="flex-1 p-4" style={{ minWidth: 0 }}>
              {!loading && !pinsLoading && (
                <ExpansionMap
                  activeStates={mapStates}
                  cityPins={[...cityPins, ...searchPins]}
                  clickToAddActive={clickToAddActive}
                  onMapClick={handleMapClick}
                  onPinClick={(pin) => {
                    if (pin.id.startsWith('search-')) return;
                    setSelectedPin((prev) => prev?.id === pin.id ? null : pin);
                  }}
                  selectedPinId={selectedPin?.id ?? null}
                  aiScores={showAiIntelligence ? aiScores : {}}
                  showAiScores={showAiIntelligence}
                  overlayLayers={overlayLayers}
                  hailData={hailData}
                  censusData={censusData}
                  competitorData={competitorData}
                  businessData={businessData}
                  crewData={crewData}
                  stormPins={stormPins}
                />
              )}
              {(loading || pinsLoading) && (
                <div className="flex items-center justify-center h-full rounded-xl" style={{ background: '#f1f5f9' }}>
                  <div className="flex flex-col items-center gap-3">
                    <div className="w-10 h-10 rounded-full border-[3px] border-[#E89A3C] border-t-transparent animate-spin" />
                    <span className="text-sm text-gray-500">Loading map data...</span>
                  </div>
                </div>
              )}
            </div>

            {/* Side Panel: Pin Detail */}
            {selectedPin && !pendingCoords && (
              <div className="w-72 flex-shrink-0 p-4 border-l overflow-y-auto" style={{ borderColor: '#F3F4F6' }}>
                <PinDetailPanel
                  pin={selectedPin}
                  onClose={() => setSelectedPin(null)}
                  onStatusChange={handleStatusChange}
                  onRingToggle={handleRingToggle}
                  onDelete={handleDeletePin}
                  saving={pinSaving}
                  pipelineStats={cityPipelineStats}
                  showAiIntelligence={showAiIntelligence}
                  aiScore={aiScores[selectedPin.id]}
                />
              </div>
            )}

            {/* Side Panel: Ring Legend */}
            {!selectedPin && !pendingCoords && cityPins.length > 0 && (
              <div className="w-56 flex-shrink-0 p-4 border-l" style={{ borderColor: '#F3F4F6' }}>
                <div className="text-xs font-bold uppercase tracking-wide mb-2" style={{ color: '#6B7280' }}>Territory Rings</div>
                <div className="space-y-2 mb-4">
                  {ALL_RADII.map((r) => {
                    const rc = RING_CONFIG[r];
                    return (
                      <div key={r} className="flex items-center gap-2">
                        <div className="w-8 h-0.5 rounded" style={{ background: rc.color, borderTop: `2px dashed ${rc.color}` }} />
                        <span className="text-xs font-medium" style={{ color: '#374151' }}>{rc.label} radius</span>
                      </div>
                    );
                  })}
                </div>
                {showAiIntelligence && Object.keys(aiScores).length > 0 && (
                  <>
                    <div className="text-xs font-bold uppercase tracking-wide mb-2" style={{ color: '#6B7280' }}>AI Score Legend</div>
                    <div className="space-y-1.5 mb-4">
                      {[{ color: '#10B981', label: '75–100 High Opportunity' }, { color: '#F59E0B', label: '50–74 Moderate' }, { color: '#EF4444', label: '1–49 Low' }].map((item) => (
                        <div key={item.label} className="flex items-center gap-2">
                          <div className="w-4 h-4 rounded-full flex-shrink-0" style={{ background: item.color }} />
                          <span className="text-[10px] font-medium" style={{ color: '#374151' }}>{item.label}</span>
                        </div>
                      ))}
                    </div>
                  </>
                )}
                {activeOverlayCount > 0 && (
                  <>
                    <div className="text-xs font-bold uppercase tracking-wide mb-2" style={{ color: '#6B7280' }}>Overlay Legend</div>
                    <div className="space-y-1.5 mb-4">
                      {OVERLAY_CONFIG.filter((o) => overlayLayers[o.key]).map((o) => (
                        <div key={o.key} className="flex items-center gap-2">
                          <div className="w-4 h-4 rounded-sm flex-shrink-0" style={{ background: o.color, opacity: 0.7 }} />
                          <span className="text-[10px] font-medium" style={{ color: '#374151' }}>{o.label}</span>
                        </div>
                      ))}
                    </div>
                  </>
                )}
                <div className="text-xs font-bold uppercase tracking-wide mb-3" style={{ color: '#6B7280' }}>City Pins</div>
                <div className="space-y-1.5">
                  {cityPins.slice(0, 8).map((p) => {
                    const cfg = STATUS_CONFIG[p.city_status] ?? STATUS_CONFIG['target-market'];
                    return (
                      <button
                        key={p.id}
                        onClick={() => setSelectedPin(p)}
                        className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-left hover:bg-gray-50 transition-all"
                      >
                        <span className="text-sm">{cfg.emoji}</span>
                        <div className="min-w-0 flex-1">
                          <div className="text-xs font-semibold truncate" style={{ color: '#1C2E5E' }}>{p.city_name}</div>
                          <div className="text-xs" style={{ color: '#9CA3AF' }}>{p.state}</div>
                        </div>
                        {showAiIntelligence && aiScores[p.id] != null && (
                          <span className="text-[10px] font-black flex-shrink-0" style={{ color: aiScores[p.id] >= 75 ? '#10B981' : aiScores[p.id] >= 50 ? '#F59E0B' : '#EF4444' }}>
                            {aiScores[p.id]}
                          </span>
                        )}
                      </button>
                    );
                  })}
                  {cityPins.length > 8 && (
                    <div className="text-xs text-center py-1" style={{ color: '#9CA3AF' }}>+{cityPins.length - 8} more</div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ─── Deep Dive Panel ─────────────────────────────────────────────────── */}
        {selectedCity && (
          <div
            id="deep-dive-panel"
            className="rounded-2xl overflow-hidden"
            style={{ background: '#fff', boxShadow: '0 4px 24px rgba(0,0,0,0.1)', border: '1px solid #E5E7EB' }}
          >
            <div className="flex items-center justify-between px-6 py-4" style={{ background: 'linear-gradient(135deg, #1C2E5E 0%, #1C2E5E 100%)' }}>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.15)' }}>
                  <Icon name="MagnifyingGlassCircleIcon" size={20} className="text-white" />
                </div>
                <div>
                  <h2 className="text-base font-bold text-white">Ground Truth Analysis — {selectedCity.city}, {selectedCity.state}</h2>
                  <p className="text-xs" style={{ color: 'rgba(255,255,255,0.6)' }}>Supply chain, labor pool &amp; compliance deep dive</p>
                </div>
              </div>
              <button
                onClick={() => setSelectedCity(null)}
                className="w-8 h-8 rounded-lg flex items-center justify-center transition-all hover:bg-white/10"
                style={{ color: 'rgba(255,255,255,0.7)' }}
              >
                <Icon name="XMarkIcon" size={18} />
              </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 divide-y lg:divide-y-0 lg:divide-x" style={{ divideColor: '#E5E7EB' }}>
              {/* Panel A: Supply Chain & Labor */}
              <div className="p-6">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: '#EFF6FF' }}>
                    <Icon name="TruckIcon" size={14} style={{ color: '#1C2E5E' }} />
                  </div>
                  <h3 className="text-sm font-bold" style={{ color: '#1C2E5E' }}>Panel A: Supply Chain &amp; Labor</h3>
                </div>
                <div className="rounded-xl overflow-hidden mb-4" style={{ height: '220px', border: '1px solid #E5E7EB' }}>
                  <SupplierMap
                    cityLat={STATE_COORDS[selectedCity.city]?.lat ?? 39.5}
                    cityLng={STATE_COORDS[selectedCity.city]?.lng ?? -98.35}
                    suppliers={selectedCity.supplier_locations}
                    cityName={selectedCity.city}
                  />
                </div>
                <div className="flex flex-wrap gap-2 mb-4">
                  {selectedCity.supplier_locations.map((s) => (
                    <div key={s.name} className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg" style={{ background: '#F9FAFB', border: '1px solid #E5E7EB' }}>
                      <div className="w-2.5 h-2.5 rounded-full" style={{ background: s.name === 'ABC Supply' ? '#E89A3C' : s.name === 'Beacon Roofing' ? '#1C2E5E' : '#10B981' }} />
                      <span className="text-xs font-medium" style={{ color: '#374151' }}>{s.name}</span>
                      <span className="text-xs" style={{ color: '#9CA3AF' }}>{s.miles} mi</span>
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-xl p-4" style={{ background: '#F9FAFB', border: '1px solid #E5E7EB' }}>
                    <div className="text-2xl font-black" style={{ color: '#1C2E5E' }}>{selectedCity.labor_pool.estimated_crews}</div>
                    <div className="text-xs mt-1" style={{ color: '#6B7280' }}>Est. Local Roofing Crews</div>
                  </div>
                  <div className="col-span-2 rounded-xl p-4 flex items-center justify-between" style={{ background: '#F9FAFB', border: '1px solid #E5E7EB' }}>
                    <span className="text-xs" style={{ color: '#6B7280' }}>Crew Availability</span>
                    <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{
                      background: selectedCity.labor_pool.crew_availability === 'Very High' || selectedCity.labor_pool.crew_availability === 'High' ? '#ECFDF5' : '#FEF3C7',
                      color: selectedCity.labor_pool.crew_availability === 'Very High' || selectedCity.labor_pool.crew_availability === 'High' ? '#10B981' : '#D97706',
                    }}>
                      {selectedCity.labor_pool.crew_availability}
                    </span>
                  </div>
                </div>
              </div>

              {/* Panel B: Compliance & Jurisdiction */}
              <div className="p-6">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: '#FEF3C7' }}>
                    <Icon name="ShieldCheckIcon" size={14} style={{ color: '#D97706' }} />
                  </div>
                  <h3 className="text-sm font-bold" style={{ color: '#1C2E5E' }}>Panel B: Compliance &amp; Jurisdiction</h3>
                </div>
                <div className="rounded-xl p-4 mb-4" style={{ background: '#F9FAFB', border: '1px solid #E5E7EB' }}>
                  <div className="flex items-center gap-2 mb-3">
                    <Icon name="DocumentTextIcon" size={14} style={{ color: '#1C2E5E' }} />
                    <span className="text-xs font-bold uppercase tracking-wide" style={{ color: '#1C2E5E' }}>State Licensing</span>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs" style={{ color: '#6B7280' }}>GC License Required</span>
                      <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{
                        background: selectedCity.compliance.gc_license_required ? '#FEE2E2' : '#ECFDF5',
                        color: selectedCity.compliance.gc_license_required ? '#EF4444' : '#10B981',
                      }}>
                        {selectedCity.compliance.gc_license_required ? 'Yes — Required' : 'No — Not Required'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs" style={{ color: '#6B7280' }}>State Insurance Min.</span>
                      <span className="text-xs font-semibold" style={{ color: '#1C2E5E' }}>{selectedCity.compliance.state_insurance_minimum}</span>
                    </div>
                  </div>
                </div>
                <div className="rounded-xl p-4 mb-4" style={{ background: '#F9FAFB', border: '1px solid #E5E7EB' }}>
                  <div className="flex items-center gap-2 mb-3">
                    <Icon name="ClipboardDocumentCheckIcon" size={14} style={{ color: '#1C2E5E' }} />
                    <span className="text-xs font-bold uppercase tracking-wide" style={{ color: '#1C2E5E' }}>Local Building Codes</span>
                  </div>
                  <div className="space-y-2">
                    {[
                      { label: 'Ice & Water Shield Mandatory on Eaves', value: selectedCity.compliance.ice_water_shield_mandatory },
                      { label: 'Drip Edge Required by Code', value: selectedCity.compliance.drip_edge_required },
                    ].map((item) => (
                      <div key={item.label} className="flex items-center gap-2.5">
                        <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: item.value ? '#ECFDF5' : '#F3F4F6' }}>
                          {item.value ? <Icon name="CheckIcon" size={11} style={{ color: '#10B981' }} /> : <Icon name="XMarkIcon" size={11} style={{ color: '#9CA3AF' }} />}
                        </div>
                        <span className="text-xs" style={{ color: '#374151' }}>{item.label}</span>
                      </div>
                    ))}
                    <div className="flex items-center justify-between pt-1">
                      <span className="text-xs" style={{ color: '#6B7280' }}>Max Shingle Layers Allowed</span>
                      <span className="text-xs font-bold" style={{ color: '#1C2E5E' }}>{selectedCity.compliance.max_shingle_layers} Layers</span>
                    </div>
                  </div>
                </div>
                <div className="rounded-xl p-4" style={{ background: 'linear-gradient(135deg, #EFF6FF 0%, #F0FDF4 100%)', border: '1px solid #DBEAFE' }}>
                  <div className="flex items-center gap-2 mb-2">
                    <Icon name="ClockIcon" size={14} style={{ color: '#1C2E5E' }} />
                    <span className="text-xs font-bold uppercase tracking-wide" style={{ color: '#1C2E5E' }}>Permitting Friction</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs" style={{ color: '#6B7280' }}>Avg Permit Approval Time</span>
                    <div className="flex items-center gap-1.5">
                      <span className="text-xl font-black" style={{ color: '#1C2E5E' }}>{selectedCity.compliance.avg_permit_days}</span>
                      <span className="text-xs font-semibold" style={{ color: '#6B7280' }}>Days</span>
                      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full ml-1" style={{
                        background: selectedCity.compliance.avg_permit_days <= 3 ? '#ECFDF5' : selectedCity.compliance.avg_permit_days <= 5 ? '#FEF3C7' : '#FEE2E2',
                        color: selectedCity.compliance.avg_permit_days <= 3 ? '#10B981' : selectedCity.compliance.avg_permit_days <= 5 ? '#D97706' : '#EF4444',
                      }}>
                        {selectedCity.compliance.avg_permit_days <= 3 ? 'Fast' : selectedCity.compliance.avg_permit_days <= 5 ? 'Moderate' : 'Slow'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ─── All Markets Summary Table ────────────────────────────────────────── */}
        <div
          className="rounded-2xl overflow-hidden"
          style={{ background: '#fff', boxShadow: '0 2px 16px rgba(0,0,0,0.07)', border: '1px solid #E5E7EB' }}
        >
          <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: '#F3F4F6' }}>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: '#F3F4F6' }}>
                <Icon name="TableCellsIcon" size={16} style={{ color: '#374151' }} />
              </div>
              <h2 className="text-sm font-bold" style={{ color: '#1C2E5E' }}>All Markets Overview</h2>
            </div>
            <span className="text-xs px-3 py-1 rounded-full font-semibold" style={{ background: '#F3F4F6', color: '#6B7280' }}>
              {markets.length} Markets Tracked
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr style={{ background: '#F9FAFB', borderBottom: '1px solid #E5E7EB' }}>
                  {['City', 'State', 'Viability', 'Pop. Growth', 'Home Age', 'Hail Events', 'Annual Replacements', 'Status', 'Action'].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide" style={{ color: '#6B7280' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {markets.map((m, i) => {
                  const sc = getScoreColor(m.viability_score);
                  return (
                    <tr key={m.id} className="transition-colors hover:bg-gray-50" style={{ borderBottom: i < markets.length - 1 ? '1px solid #F3F4F6' : 'none' }}>
                      <td className="px-4 py-3 text-sm font-semibold" style={{ color: '#1C2E5E' }}>{m.city}</td>
                      <td className="px-4 py-3 text-sm" style={{ color: '#374151' }}>{m.state}</td>
                      <td className="px-4 py-3"><span className="text-sm font-black" style={{ color: sc }}>{m.viability_score}/100</span></td>
                      <td className="px-4 py-3 text-sm font-semibold" style={{ color: '#10B981' }}>+{m.population_growth}%</td>
                      <td className="px-4 py-3 text-sm" style={{ color: '#374151' }}>{m.median_home_age} yrs</td>
                      <td className="px-4 py-3 text-sm" style={{ color: '#374151' }}>{m.hail_events_5yr}</td>
                      <td className="px-4 py-3 text-sm" style={{ color: '#374151' }}>{formatNumber(m.annual_replacements_needed)}</td>
                      <td className="px-4 py-3">
                        <span className="text-xs font-bold px-2 py-1 rounded-full" style={{
                          background: m.is_active_market ? '#ECFDF5' : '#EFF6FF',
                          color: m.is_active_market ? '#10B981' : '#1C2E5E',
                        }}>
                          {m.is_active_market ? '✓ Active' : '🎯 Recommended'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => handleDeepDive(m)}
                          className="text-xs font-semibold px-3 py-1.5 rounded-lg transition-all hover:opacity-90"
                          style={{ background: '#E89A3C', color: '#fff' }}
                        >
                          Deep Dive
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* ─── Market Search Panel ──────────────────────────────────────────────── */}
        <MarketSearchPanel
          onSaveToExpansion={handleSaveSearchedMarket}
          onSearchPinsChange={setSearchPins}
          existingPins={cityPins}
        />

        {/* ─── Expansion Priority List ──────────────────────────────────────────── */}
        <ExpansionPriorityList
          initialItems={priorityItems.length > 0 ? priorityItems : undefined}
          onItemsChange={setPriorityItems}
        />

        {/* ─── City Competitor Search ───────────────────────────────────────────── */}
        <CityCompetitorSearch
          onAddToTracker={(competitor) => setPendingExternalCompetitor(competitor)}
          trackedIds={trackedCompetitorIds}
        />

        {/* ─── Competitor Intelligence Manager ──────────────────────────────────────── */}
        <CompetitorLocationsManager
          onCompetitorsChange={(list) => {
            setCompetitorData(list as any);
            setTrackedCompetitorIds(new Set(list.map((c) => c.id)));
            setTrackedCompetitors(list);
          }}
          externalCompetitor={pendingExternalCompetitor}
          onExternalAdded={() => setPendingExternalCompetitor(null)}
        />

        {/* ─── Competitor Visualization Charts ──────────────────────────────────── */}
        <div className="mt-6">
          <CompetitorVisualizationCharts competitors={trackedCompetitors} />
        </div>

        {/* ─── Threat Tier System ────────────────────────────────────────────── */}
        <div className="mt-6 rounded-2xl overflow-hidden" style={{ border: '1px solid #E2E8F0', background: '#fff' }}>
          <div
            className="flex items-center gap-3 px-5 py-4"
            style={{ background: 'linear-gradient(135deg, #1E3A5F 0%, #1C2E5E 100%)', borderBottom: '1px solid #E2E8F0' }}
          >
            <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.15)' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
            </div>
            <div>
              <h3 className="text-sm font-bold text-white">Automated Threat Tier System</h3>
              <p className="text-xs text-blue-200 mt-0.5">Competitors ranked by market impact, size &amp; proximity — red alerts on high-threat entries</p>
            </div>
          </div>
          <div className="p-5">
            <ThreatTierSystem competitors={trackedCompetitors} />
          </div>
        </div>

        {/* ── Gemini Market Intelligence ───────────────────────────────────────── */}
        <MarketIntelligencePanel
          cityPins={cityPins}
          businessData={businessData}
          competitorData={competitorData}
          hailData={hailData}
        />

        {/* ── AI Prediction Validator (Census + NOAA) ───────────────────────────── */}
        <div className="mt-6">
          <MarketValidationPanel />
        </div>

        {/* ── Gemini Cluster Analysis ──────────────────────────────────────────── */}
        <ClusterAnalysisPanel
          markets={markets}
          priorityItems={priorityItems}
        />

      </div>

      {/* ── Add City Modal ──────────────────────────────────────────────────── */}
      {pendingCoords && (
        <AddCityModal
          lat={pendingCoords.lat}
          lng={pendingCoords.lng}
          onSave={handleAddPin}
          onCancel={() => setPendingCoords(null)}
          saving={pinSaving}
        />
      )}

      {/* ── Full Screen Backdrop Escape ── */}
      {isFullScreen && (
        <button
          onClick={() => setIsFullScreen(false)}
          className="fixed top-4 right-4 z-[9991] w-9 h-9 rounded-xl flex items-center justify-center text-white transition-all hover:opacity-80"
          style={{ background: 'rgba(0,0,0,0.6)' }}
          title="Exit Full Screen"
        >
          <Icon name="XMarkIcon" size={18} />
        </button>
      )}
    </div>
  );
};

export default ExpansionMarketInteractive;
